<script lang="ts">
  import { ButtonColor, IconType } from "$types/atoms";
  import Icon from "$lib/atoms/Icon.svelte";
  import { createEventDispatcher } from "svelte";
  import { parseUrl } from "$lib/utils/helpers";
  import { textDir } from "$stores";
  import type { Link } from "$types/data";

  export let icon: IconType | null = null;
  export let rounded = false;
  export let reduced = false;
  export let tiny = false;
  export let color: ButtonColor | string = ButtonColor.BLACK;
  export let colorMd: ButtonColor | null = null;
  export let colorLg: ButtonColor | null = null;
  export let iconClasses = "";
  export let href: string | Link | null = null;
  export let target: "_blank" | undefined = undefined;
  export let textOutside = false;
  export let iconLeft = false;
  export let disableArrow = false;
  export let disable = false;
  export let disableButtonHoverAnimation = false;
  export let nowrap = false;
  export let fullWidth = false;
  export let type: "button" | "submit" | undefined = undefined;
  export let label: string | null = null;

  const tag = href ? "a" : "button";

  const dispatch = createEventDispatcher();

  $: hasArrow = icon === IconType.ARROW_RIGHT || IconType.ARROW_LEFT;
  $: tagSpecificAttributes = href
    ? { href: parseUrl(href) }
    : { disabled: disable };

  const onClick = () => {
    dispatch("click", {});
  };

  function getClasses() {
    let className = "";
    className += reduced ? " reduced" : "";
    className += tiny ? " tiny" : "";
    className += ` color-${color} color-md-${
      colorMd ? colorMd : color
    } color-lg-${colorLg ? colorLg : color}`;
    className += ` ${
      icon === IconType.ARROW_RIGHT && disableArrow ? "large-icon" : ""
    }`;

    return className;
  }
</script>

<svelte:element
  this={tag}
  {...tagSpecificAttributes}
  class={`button ${getClasses()} ${$$restProps.class || ""} `}
  class:border-radius-[var(--border-radius-small)]={!rounded}
  class:disable
  class:rounded-lg={rounded}
  class:has-icon={icon}
  class:has-text={$$slots.default}
  class:has-text-outside={$$slots.default && textOutside}
  class:has-arrow={hasArrow}
  class:full-width={fullWidth}
  {type}
  on:keyup={() => undefined}
  on:click={onClick}
  {target}
  aria-label={label}
  role="button"
  tabindex="0"
>
  {#if icon && iconLeft}
    <Icon classes={iconClasses} type={icon} />
  {/if}
  {#if $$slots.default && !textOutside}
    <span
      class="text-inside"
      class:type:button-reduced={reduced}
      class:type:button={!reduced}
      dir={$textDir}
      class:nowrap
    >
      <span class="initial-text"><slot /></span>
      <span class="hover-text"><slot /></span>
    </span>
  {:else if $$slots.default && textOutside}
    <span
      class="text-outside"
      class:type:button-reduced={reduced}
      class:type:button={!reduced}
      dir={$textDir}><slot /></span
    >
  {/if}
  <span class="icon-wrapper {iconClasses}">
    {#if icon && !iconLeft}
      <span class={`${!disableButtonHoverAnimation ? "initial-arrow" : ""}`}>
        <Icon type={icon} />
      </span>

      {#if hasArrow && disableButtonHoverAnimation !== true}
        <span class="hover-arrow">
          <Icon type={icon} />
        </span>
      {/if}
    {/if}
  </span>
</svelte:element>

<style lang="scss">
  .button {
    padding: 0 20px;
    height: 66px;
    min-width: 72px;
    text-decoration: none;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    color: rgb(var(--color-white));
    cursor: pointer;
    width: max-content;
    position: relative;
    transition: background 0.3s;
    font-family: var(--font-primary);

    &:not(.has-text-outside) {
      overflow: hidden;
    }

    .icon-wrapper {
      position: relative;
      display: flex;
      align-items: center;

      .initial-arrow {
        display: inline-block;
        transition:
          transform 0.3s ease-in-out,
          opacity 0.2s 0.1s;
      }

      .hover-arrow {
        position: absolute;
        top: 0;
        left: 0;
        transform: translateX(-30px);
        opacity: 0;
        transition:
          transform 0.3s ease-in-out,
          opacity 0.2s 0.1s;
      }
    }

    &.has-arrow:hover {
      .hover-arrow {
        opacity: 1;
        transform: translateX(0);
      }

      .initial-arrow {
        opacity: 0;
        transform: translateX(30px);
      }
    }

    :global(svg) {
      color: rgb(var(--color-white));
      transition: color 0.3s;
    }

    &.reduced {
      height: 36px;
      min-width: 56px;
      padding: 0 16px;
    }

    &.tiny {
      height: 28px;
      min-width: 48px;
    }

    &.bordered {
      border: 1px solid rgba(0, 0, 0, 0.1);
    }

    &.outlined {
      border: 1px solid rgb(var(--color-black));
    }

    &.outlined-light {
      border: 1px solid rgb(var(--color-white));
    }

    &.outlined-light-thicc {
      border: 2px solid rgb(var(--color-white));
    }

    .text-inside {
      position: relative;

      .initial-text {
        display: inline-block;
        transition:
          transform 0.3s ease-in-out,
          opacity 0.2s 0.1s;
      }

      .hover-text {
        transition:
          transform 0.3s ease-in-out,
          opacity 0.2s 0.1s;

        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
        transform: translateY(20px);
      }
    }

    &:hover {
      .text-inside {
        .initial-text {
          transform: translateY(-20px);
          opacity: 0;
        }

        .hover-text {
          transform: translateY(0px);
          opacity: 1;
        }
      }
    }

    .text-outside {
      position: absolute;
      right: 0;
      transform: translateX(100%);
      padding-left: fluid(16px, 16px);
      white-space: nowrap;
      height: 100%;
      top: 0;
      display: flex;
      align-items: center;
    }

    &.color-black {
      background: rgb(var(--color-black));
    }

    &.color-green {
      background: rgb(var(--color-light-green));
    }

    &.color-dark-green {
      background: rgb(var(--color-green-primary));
    }

    &.color-sand {
      background: rgb(var(--color-warm-grey));
      color: rgb(var(--color-text-dark));
      :global(svg) {
        color: rgb(var(--color-text-dark));
      }
    }

    &.color-dark-sand {
      background: #dbd7ce;
      color: rgb(var(--color-text-dark));
      :global(svg) {
        color: rgb(var(--color-text-dark));
      }
    }

    &.color-white-light {
      background: var(--color-stroke-light-rgba);
      color: rgb(var(--color-text-dark));

      :global(svg) {
        color: rgb(var(--color-white));
      }
    }

    &.color-white-faded {
      background: var(--color-stroke-light-rgba);
      color: inherit;

      :global(svg) {
        color: inherit;
      }
    }

    &.color-white-faded-light {
      color: rgb(var(--color-white));
    }

    //--color-stroke-dark-rgba: rgba(0, 0, 0, 0.1);
    &.color-dark-light {
      background: var(--color-stroke-dark-rgba);

      :global(svg) {
        color: rgb(var(--color-black));
      }
    }

    &.color-gray {
      background: rgba(0, 0, 0, 0.08);
    }

    &.color-red {
      background: rgb(var(--color-red-secondary));
    }

    &.color-yellow {
      background: rgb(var(--color-consumer-yellow));
      color: rgb(var(--color-text-dark));

      :global(svg) {
        color: rgb(var(--color-text-dark)); // Add this line
      }
    }

    &.color-white {
      background: rgb(var(--color-white));
      color: rgb(var(--color-text-dark));
    }

    &.color-md-gray {
      @include breakpoint("small") {
        background: rgba(0, 0, 0, 0.08);
        color: rgb(var(--color-text-dark));
      }
    }

    &.color-md-red {
      @include breakpoint("small") {
        background: rgb(var(--color-red-secondary));
      }
    }

    &.color-md-white {
      @include breakpoint("small") {
        background: rgb(var(--color-white));
        color: rgb(var(--color-text-dark));
      }
    }

    &.color-white,
    &.color-gray {
      color: rgb(var(--color-text-dark));

      :global(svg) {
        color: rgb(var(--color-text-dark));
      }
    }

    &.color-md-white,
    &.color-md-gray {
      @include breakpoint("small") {
        :global(svg) {
          color: rgb(var(--color-text-dark));
        }
      }
    }

    &.color-lg-white {
      @include breakpoint("medium") {
        background: rgb(var(--color-white));
        color: rgb(var(--color-text-dark));
      }
    }

    &.color-blue {
      background: rgb(var(--color-blue-primary));
      color: rgb(var(--color-white));

      :global(svg) {
        color: rgb(var(--color-white));
      }
    }
  }

  .full-width {
    width: 100%;
  }

  .nowrap {
    white-space: nowrap;
  }

  :global(.hero.green),
  :global(.hero.gray) {
    .color-inherit {
      background: rgb(var(--color-black));
    }
  }

  :global(.hero.yellow),
  :global(.hero.blue) {
    .color-inherit {
      background: rgb(var(--color-white));
      color: rgb(var(--color-text-dark));
    }
  }

  :global(span.text-inside + svg),
  :global(svg + span.text-inside) {
    margin-left: em(10px, 14px);
  }

  :global(.reduced span.text-inside + svg),
  :global(.reduced svg + span.text-inside) {
    margin-left: em(10px, 20px);
  }

  :global(svg) {
    width: 22px;
    height: 22px;
    color: currentColor;
  }

  :global(.reduced svg) {
    width: 18px;
    height: 18px;
  }

  :global(.large-icon:not(.reduced) svg) {
    width: fluid(24px, 24px);
    height: fluid(24px, 24px);
  }

  .button.disable {
    opacity: 0.4;
    pointer-events: none;
  }
</style>
